import { forwardRef, type KeyboardEvent } from 'react';

import type React from 'react';

import { useAppTranslations } from '@treneo/shared/i18n';
import { IconSizes, SearchIcon } from '@treneo/shared/icons';
import { Colors } from '@treneo/shared/themes';
import { styled } from '@treneo/shared/ui';

const SearchInputWrapper = styled('div')`
  display: flex;
  align-items: center;
  width: 100%;
`;

const SearchIconWrapper = styled('div')`
  display: flex;
  align-items: center;
  color: ${Colors.darkGrey};
  margin-right: 8px;
`;

const StyledInput = styled('input')`
  border: none;
  outline: none;
  background: transparent;
  flex: 1;
  min-width: 120px;
  font-size: 14px;
  padding: 4px 0;

  &::placeholder {
    color: ${Colors.darkGrey};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

type SearchInputProps = Readonly<{
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
}>;

const SearchInputComponent = (
  {
    value,
    onChange,
    onKeyDown,
    onFocus,
    placeholder,
    disabled = false,
    hasError = false
  }: SearchInputProps,
  ref: React.Ref<HTMLInputElement>
) => {
  const { t } = useAppTranslations();

  return (
    <SearchInputWrapper>
      <SearchIconWrapper>
        <SearchIcon fontSize={IconSizes.small} />
      </SearchIconWrapper>
      <StyledInput
        ref={ref}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={t('coursesPage.searchPlaceholder')}
        aria-invalid={hasError}
        role='textbox'
      />
    </SearchInputWrapper>
  );
};

SearchInputComponent.displayName = 'SearchInput';

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(SearchInputComponent);
